import { ErrorCode } from './types'

/**
 * Plafond de temps cumulé raisonnable (1 an). Au-delà, on considère que le
 * module/driver SCORM a envoyé une durée aberrante (ex. horloge système fausse)
 * et on ignore la valeur plutôt que de stocker un temps impossible.
 */
const MAX_TOTAL_TIME_MS = 366 * 24 * 60 * 60 * 1000

/** Garde-fou de plage pour les scores absolus (raw/min/max). */
const MAX_SCORE_ABS = 1_000_000

/**
 * Parse et valide un score SCORM. Renvoie null si la valeur est absente, non
 * finie (NaN/Infinity) ou hors d'une plage plausible — pour éviter de propager
 * des valeurs poubelle (ex. 3.99e+49) émises par un module/driver buggé.
 */
function sanitizeScore(value: string | undefined, max = MAX_SCORE_ABS): number | null {
  if (!value) return null
  const n = Number.parseFloat(value)
  if (!Number.isFinite(n) || Math.abs(n) > max) return null
  return n
}

/** Parse an ISO 8601 duration (e.g. PT1H30M45.5S) to milliseconds */
function iso8601DurationToMs(duration: string): number {
  const match = duration.match(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?$/)
  if (!match) return 0
  const hours = Number.parseInt(match[4] || '0', 10)
  const minutes = Number.parseInt(match[5] || '0', 10)
  const seconds = Number.parseFloat(match[6] || '0')
  const ms = ((hours * 3600) + (minutes * 60) + seconds) * 1000
  // Borne les durées aberrantes (horloge système fausse côté contenu SCORM).
  if (!Number.isFinite(ms) || ms < 0 || ms > MAX_TOTAL_TIME_MS) return 0
  return ms
}

/** Convert milliseconds to ISO 8601 duration (PT#H#M#.##S) */
function msToIso8601Duration(ms: number): string {
  const totalSeconds = Math.round(ms / 10) / 100 // round to centiseconds
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Number.parseFloat((totalSeconds % 60).toFixed(2))
  return `PT${hours}H${minutes}M${seconds}S`
}

/**
 * Parse a SCORM 1.2 CMITimespan (HHHH:MM:SS[.SS]) to milliseconds.
 * Renvoie null si le format est invalide ou la durée aberrante.
 */
function cmiTimespanToMs(timespan: string): number | null {
  const match = timespan.match(/^(\d{2,4}):(\d{2}):(\d{2}(?:\.\d{1,2})?)$/)
  if (!match) return null
  const hours = Number.parseInt(match[1], 10)
  const minutes = Number.parseInt(match[2], 10)
  const seconds = Number.parseFloat(match[3])
  const ms = ((hours * 3600) + (minutes * 60) + seconds) * 1000
  if (!Number.isFinite(ms) || ms < 0 || ms > MAX_TOTAL_TIME_MS) return null
  return ms
}

/** Convert milliseconds to a SCORM 1.2 CMITimespan (HHHH:MM:SS[.SS]) */
function msToCmiTimespan(ms: number): string {
  const totalCentis = Math.round(ms / 10)
  const centis = totalCentis % 100
  const totalSeconds = Math.floor(totalCentis / 100)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const base = `${String(hours).padStart(4, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return centis > 0 ? `${base}.${String(centis).padStart(2, '0')}` : base
}

export type ScormData = {
  completionStatus?: string
  successStatus?: string
  scoreRaw?: number | null
  scoreMin?: number | null
  scoreMax?: number | null
  scoreScaled?: number | null
  totalTime?: string | null
  sessionTime?: string | null
  suspendData?: string | null
  location?: string | null
}

type OnCommitCallback = (data: ScormData) => Promise<void>

/** Notifié quand le contenu appelle Terminate/LMSFinish (fin de session côté module). */
type OnTerminateCallback = (info: { completed: boolean }) => void

export class ScormApiBridge {
  private data: Record<string, string> = {}
  private initialized = false
  private terminated = false
  private lastError: ErrorCode = ErrorCode.NO_ERROR
  private onCommit: OnCommitCallback
  private onTerminate?: OnTerminateCallback
  private initialTotalTimeMs: number
  // Vrai si le contenu a posé cmi.success_status lui-même pendant cette
  // session. La valeur seedée depuis la DB (resume) reste lisible via GetValue
  // mais n'est pas renvoyée au serveur : elle peut avoir été dérivée du score,
  // la réémettre la figerait comme si le module l'avait posée.
  private successSetByContent = false
  private commitQueue: Promise<void>[] = []

  constructor(initialData: ScormData | null, onCommit: OnCommitCallback, onTerminate?: OnTerminateCallback) {
    this.onCommit = onCommit
    this.onTerminate = onTerminate
    this.initialTotalTimeMs = 0

    if (initialData) {
      if (initialData.completionStatus) this.data['cmi.completion_status'] = initialData.completionStatus.toLowerCase().replace('_', ' ')
      if (initialData.successStatus) this.data['cmi.success_status'] = initialData.successStatus.toLowerCase()
      if (initialData.scoreRaw != null) this.data['cmi.score.raw'] = String(initialData.scoreRaw)
      if (initialData.scoreMin != null) this.data['cmi.score.min'] = String(initialData.scoreMin)
      if (initialData.scoreMax != null) this.data['cmi.score.max'] = String(initialData.scoreMax)
      if (initialData.scoreScaled != null) this.data['cmi.score.scaled'] = String(initialData.scoreScaled)
      if (initialData.totalTime) {
        this.data['cmi.total_time'] = initialData.totalTime
        this.initialTotalTimeMs = iso8601DurationToMs(initialData.totalTime)
      }
      if (initialData.suspendData) this.data['cmi.suspend_data'] = initialData.suspendData
      if (initialData.location) this.data['cmi.location'] = initialData.location
      this.data['cmi.entry'] = initialData.suspendData || initialData.location ? 'resume' : 'ab-initio'
    } else {
      this.data['cmi.entry'] = 'ab-initio'
    }

    this.data['cmi.completion_status'] = this.data['cmi.completion_status'] || 'not attempted'
    this.data['cmi.success_status'] = this.data['cmi.success_status'] || 'unknown'
    this.data['cmi.exit'] = ''
    this.data['cmi.mode'] = 'normal'
    this.data['cmi.credit'] = 'credit'
  }

  Initialize(_param: string = ''): string {
    if (this.initialized) {
      this.lastError = ErrorCode.ALREADY_INITIALIZED
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.CONTENT_INSTANCE_TERMINATED
      return 'false'
    }
    this.initialized = true
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  Terminate(_param: string = ''): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.TERMINATE_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.TERMINATE_AFTER_TERMINATE
      return 'false'
    }

    this.Commit()
    this.terminated = true
    this.initialized = false
    this.lastError = ErrorCode.NO_ERROR
    this.onTerminate?.({
      completed: this.data['cmi.completion_status'] === 'completed'
        || this.data['cmi.success_status'] === 'passed',
    })
    return 'true'
  }

  GetValue(key: string): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.GET_BEFORE_INIT
      return ''
    }
    if (this.terminated) {
      this.lastError = ErrorCode.GET_AFTER_TERMINATE
      return ''
    }

    this.lastError = ErrorCode.NO_ERROR

    if (key === 'cmi._version') return '1.0'
    if (key === 'cmi.learner_name') return ''
    if (key === 'cmi.learner_id') return ''

    if (key.endsWith('._count')) {
      const prefix = key.replace('._count', '')
      let count = 0
      for (const k of Object.keys(this.data)) {
        const match = k.match(new RegExp(`^${prefix.replace(/\./g, '\\.')}\\.(\\d+)\\.`))
        if (match) {
          const idx = Number.parseInt(match[1], 10) + 1
          if (idx > count) count = idx
        }
      }
      return String(count)
    }

    return this.data[key] ?? ''
  }

  SetValue(key: string, value: string): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.SET_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.SET_AFTER_TERMINATE
      return 'false'
    }

    const readOnlyKeys = ['cmi._version', 'cmi.learner_id', 'cmi.learner_name', 'cmi.entry', 'cmi.mode', 'cmi.credit']
    if (readOnlyKeys.includes(key)) {
      this.lastError = ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY
      return 'false'
    }

    this.data[key] = value
    if (key === 'cmi.success_status') this.successSetByContent = true
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  /**
   * Rollup des objectifs : si le contenu ne pose pas cmi.success_status mais
   * renseigne ses objectifs (cmi.objectives.N.success_status), une valeur
   * unique passed/failed fait foi. Des valeurs contradictoires → aucun signal
   * (on ne devine pas quel objectif est le principal).
   */
  private deriveObjectivesSuccess(): string | null {
    const values = new Set<string>()
    for (const [key, value] of Object.entries(this.data)) {
      if (/^cmi\.objectives\.\d+\.success_status$/.test(key) && (value === 'passed' || value === 'failed')) {
        values.add(value)
      }
    }
    return values.size === 1 ? [...values][0] : null
  }

  Commit(_param: string = ''): string {
    if (!this.initialized) {
      this.lastError = ErrorCode.COMMIT_BEFORE_INIT
      return 'false'
    }
    if (this.terminated) {
      this.lastError = ErrorCode.COMMIT_AFTER_TERMINATE
      return 'false'
    }

    const completionStatusMap: Record<string, string> = {
      completed: 'COMPLETED',
      incomplete: 'INCOMPLETE',
      'not attempted': 'NOT_ATTEMPTED',
      unknown: 'UNKNOWN',
    }

    const successStatusMap: Record<string, string> = {
      passed: 'PASSED',
      failed: 'FAILED',
      unknown: 'UNKNOWN',
    }

    // Accumulate total_time = previous total + current session_time (per SCORM 2004 spec)
    let accumulatedTotalTime: string | null = this.data['cmi.total_time'] || null
    const sessionTimeStr = this.data['cmi.session_time'] || null
    if (sessionTimeStr) {
      const sessionMs = iso8601DurationToMs(sessionTimeStr)
      accumulatedTotalTime = msToIso8601Duration(this.initialTotalTimeMs + sessionMs)
    }

    // Le statut de réussite n'est transmis que si le contenu l'a posé lui-même
    // (SetValue direct, sinon rollup de ses objectifs). Sans signal, le champ
    // est omis et l'API dérive PASSED/FAILED du score et du seuil du manifeste.
    const successValue = this.successSetByContent
      ? this.data['cmi.success_status']
      : this.deriveObjectivesSuccess()

    const scormData: ScormData = {
      completionStatus: completionStatusMap[this.data['cmi.completion_status'] || 'not attempted'] || 'NOT_ATTEMPTED',
      successStatus: successValue ? successStatusMap[successValue] : undefined,
      scoreRaw: sanitizeScore(this.data['cmi.score.raw']),
      scoreMin: sanitizeScore(this.data['cmi.score.min']),
      scoreMax: sanitizeScore(this.data['cmi.score.max']),
      // cmi.score.scaled est borné à [-1, 1] par la spec SCORM 2004.
      scoreScaled: sanitizeScore(this.data['cmi.score.scaled'], 1),
      totalTime: accumulatedTotalTime,
      sessionTime: sessionTimeStr,
      suspendData: this.data['cmi.suspend_data'] || null,
      location: this.data['cmi.location'] || null,
    }

    // onCommit reste appelé de manière synchrone ; on ne fait que tracker la
    // promise résultante pour que flushCommits() puisse l'attendre (ex. avant
    // un unload d'onglet qui tuerait une mutation en vol).
    const commitPromise: Promise<void> = this.onCommit(scormData)
      .catch(console.error)
      .finally(() => {
        this.commitQueue = this.commitQueue.filter(p => p !== commitPromise)
      })
    this.commitQueue.push(commitPromise)
    this.lastError = ErrorCode.NO_ERROR
    return 'true'
  }

  /**
   * Attend la résolution de tous les commits en vol (les erreurs sont déjà
   * absorbées par la queue, la promise renvoyée ne rejette jamais).
   */
  async flushCommits(): Promise<void> {
    while (this.commitQueue.length > 0) {
      await Promise.all(this.commitQueue)
    }
  }

  /** État exposé pour les adapters (ex. SCORM 1.2) qui valident avant de déléguer. */
  isInitialized(): boolean {
    return this.initialized
  }

  /** Dernier code d'erreur brut (2004), pour la traduction côté adapter 1.2. */
  getLastErrorCode(): ErrorCode {
    return this.lastError
  }

  /**
   * Réinitialise l'axe success à « non posé par le contenu » : l'adapter 1.2
   * l'appelle quand le lesson_status passe à une valeur sans signal passed/failed
   * (completed/incomplete/browsed/not attempted). Sans cela, un `failed`
   * précédent resterait figé et serait réémis à chaque commit. Une fois remis à
   * plat, Commit omet successStatus et l'API le dérive du score (cf. bridge 2004).
   */
  resetSuccessStatus(): void {
    this.data['cmi.success_status'] = 'unknown'
    this.successSetByContent = false
  }

  GetLastError(): string {
    return String(this.lastError)
  }

  GetErrorString(errorCode: string): string {
    const code = Number.parseInt(errorCode, 10)
    const errorStrings: Record<number, string> = {
      [ErrorCode.NO_ERROR]: 'No Error',
      [ErrorCode.GENERAL_EXCEPTION]: 'General Exception',
      [ErrorCode.GENERAL_INITIALIZATION_FAILURE]: 'General Initialization Failure',
      [ErrorCode.ALREADY_INITIALIZED]: 'Already Initialized',
      [ErrorCode.CONTENT_INSTANCE_TERMINATED]: 'Content Instance Terminated',
      [ErrorCode.GENERAL_TERMINATION_FAILURE]: 'General Termination Failure',
      [ErrorCode.TERMINATE_BEFORE_INIT]: 'Termination Before Initialization',
      [ErrorCode.TERMINATE_AFTER_TERMINATE]: 'Termination After Termination',
      [ErrorCode.GET_BEFORE_INIT]: 'Retrieve Data Before Initialization',
      [ErrorCode.GET_AFTER_TERMINATE]: 'Retrieve Data After Termination',
      [ErrorCode.SET_BEFORE_INIT]: 'Store Data Before Initialization',
      [ErrorCode.SET_AFTER_TERMINATE]: 'Store Data After Termination',
      [ErrorCode.COMMIT_BEFORE_INIT]: 'Commit Before Initialization',
      [ErrorCode.COMMIT_AFTER_TERMINATE]: 'Commit After Termination',
      [ErrorCode.UNDEFINED_DATA_MODEL_ELEMENT]: 'Undefined Data Model Element',
      [ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY]: 'Data Model Element Is Read Only',
      [ErrorCode.DATA_MODEL_ELEMENT_IS_WRITE_ONLY]: 'Data Model Element Is Write Only',
    }
    return errorStrings[code] || 'Unknown Error'
  }

  GetDiagnostic(errorCode: string): string {
    return this.GetErrorString(errorCode)
  }
}

// --- SCORM 1.2 adapter ---

/** Codes d'erreur du runtime SCORM 1.2 (espace distinct des codes 2004). */
const SCORM12_ERROR_STRINGS: Record<number, string> = {
  0: 'No error',
  101: 'General exception',
  201: 'Invalid argument error',
  202: 'Element cannot have children',
  203: 'Element not an array - cannot have count',
  301: 'Not initialized',
  401: 'Not implemented error',
  402: 'Invalid set value, element is a keyword',
  403: 'Element is read only',
  404: 'Element is write only',
  405: 'Incorrect data type',
}

/** Traduction des codes d'erreur 2004 du bridge vers l'espace 1.2. */
const SCORM2004_TO_12_ERRORS: Record<number, number> = {
  [ErrorCode.NO_ERROR]: 0,
  [ErrorCode.GENERAL_EXCEPTION]: 101,
  [ErrorCode.GENERAL_INITIALIZATION_FAILURE]: 101,
  [ErrorCode.ALREADY_INITIALIZED]: 101,
  [ErrorCode.CONTENT_INSTANCE_TERMINATED]: 101,
  [ErrorCode.GENERAL_TERMINATION_FAILURE]: 101,
  // 1.2 n'a pas de notion de "terminated" : après LMSFinish, l'API n'est
  // simplement plus initialisée (301).
  [ErrorCode.TERMINATE_BEFORE_INIT]: 301,
  [ErrorCode.TERMINATE_AFTER_TERMINATE]: 301,
  [ErrorCode.GET_BEFORE_INIT]: 301,
  [ErrorCode.GET_AFTER_TERMINATE]: 301,
  [ErrorCode.SET_BEFORE_INIT]: 301,
  [ErrorCode.SET_AFTER_TERMINATE]: 301,
  [ErrorCode.COMMIT_BEFORE_INIT]: 301,
  [ErrorCode.COMMIT_AFTER_TERMINATE]: 301,
  [ErrorCode.GENERAL_ARGUMENT_ERROR]: 201,
  [ErrorCode.UNDEFINED_DATA_MODEL_ELEMENT]: 401,
  [ErrorCode.UNIMPLEMENTED_DATA_MODEL_ELEMENT]: 401,
  [ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY]: 403,
  [ErrorCode.DATA_MODEL_ELEMENT_IS_WRITE_ONLY]: 404,
  [ErrorCode.DATA_MODEL_ELEMENT_TYPE_MISMATCH]: 405,
  [ErrorCode.DATA_MODEL_ELEMENT_VALUE_OUT_OF_RANGE]: 405,
}

/** Éléments cmi.core.* 1.2 dont la valeur est identique côté 2004. */
const SCORM12_TO_2004_KEYS: Record<string, string> = {
  'cmi.core.student_id': 'cmi.learner_id',
  'cmi.core.student_name': 'cmi.learner_name',
  'cmi.core.lesson_location': 'cmi.location',
  'cmi.core.credit': 'cmi.credit',
  'cmi.core.entry': 'cmi.entry',
  'cmi.core.lesson_mode': 'cmi.mode',
  'cmi.core.exit': 'cmi.exit',
  'cmi.core.score.raw': 'cmi.score.raw',
  'cmi.core.score.min': 'cmi.score.min',
  'cmi.core.score.max': 'cmi.score.max',
}

/**
 * Projection du lesson_status 1.2 (axe unique) sur le completion_status 2004.
 * Seuls passed/failed portent un signal de réussite (voir setSuccess) ; les
 * autres valeurs remettent l'axe success à plat et laissent l'API dériver du
 * score, à l'image du bridge 2004 natif.
 */
const LESSON_STATUS_TO_2004: Record<string, { completion: string, setSuccess: 'passed' | 'failed' | null }> = {
  passed: { completion: 'completed', setSuccess: 'passed' },
  completed: { completion: 'completed', setSuccess: null },
  failed: { completion: 'completed', setSuccess: 'failed' },
  incomplete: { completion: 'incomplete', setSuccess: null },
  browsed: { completion: 'incomplete', setSuccess: null },
  'not attempted': { completion: 'not attempted', setSuccess: null },
}

const SCORM12_CORE_CHILDREN = 'student_id,student_name,lesson_location,credit,lesson_status,entry,score,total_time,lesson_mode,exit,session_time'

// SCORM 1.2 adapter: traduit les noms d'éléments (cmi.core.* → modèle 2004),
// les formats de valeurs (CMITimespan ↔ ISO 8601, lesson_status ↔ deux axes)
// et les codes d'erreur (espace 1.2) avant de déléguer au bridge 2004.
export function createScorm12Adapter(bridge: ScormApiBridge) {
  // Erreur posée par l'adapter lui-même (validations propres à 1.2) ; à défaut,
  // le dernier code du bridge est traduit vers l'espace 1.2.
  let adapterError: number | null = null

  const lastErrorCode = (): number => {
    if (adapterError !== null) return adapterError
    return SCORM2004_TO_12_ERRORS[bridge.getLastErrorCode()] ?? 101
  }

  return {
    LMSInitialize: (param: string = '') => {
      adapterError = null
      return bridge.Initialize(param)
    },

    LMSFinish: (param: string = '') => {
      adapterError = null
      return bridge.Terminate(param)
    },

    LMSGetValue: (key: string): string => {
      adapterError = null

      if (key === 'cmi.core.lesson_status') {
        const completion = bridge.GetValue('cmi.completion_status')
        if (bridge.getLastErrorCode() !== ErrorCode.NO_ERROR) return ''
        const success = bridge.GetValue('cmi.success_status')
        if (success === 'passed' || success === 'failed') return success
        if (completion === 'completed' || completion === 'incomplete') return completion
        return 'not attempted'
      }

      if (key === 'cmi.core.total_time') {
        const iso = bridge.GetValue('cmi.total_time')
        if (bridge.getLastErrorCode() !== ErrorCode.NO_ERROR) return ''
        return msToCmiTimespan(iso8601DurationToMs(iso))
      }

      if (key === 'cmi.core.session_time') {
        adapterError = bridge.isInitialized() ? 404 : 301
        return ''
      }

      if (key === 'cmi.core._children' || key === 'cmi.core.score._children') {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return ''
        }
        return key === 'cmi.core._children' ? SCORM12_CORE_CHILDREN : 'raw,min,max'
      }

      return bridge.GetValue(SCORM12_TO_2004_KEYS[key] ?? key)
    },

    LMSSetValue: (key: string, value: string): string => {
      adapterError = null

      if (key === 'cmi.core.lesson_status') {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return 'false'
        }
        const mapped = LESSON_STATUS_TO_2004[value]
        if (!mapped) {
          adapterError = 405
          return 'false'
        }
        if (bridge.SetValue('cmi.completion_status', mapped.completion) !== 'true') return 'false'
        // Seuls passed/failed portent un verdict ; sinon on remet l'axe success
        // à plat pour ne pas réémettre un passed/failed devenu obsolète.
        if (mapped.setSuccess === null) {
          bridge.resetSuccessStatus()
          return 'true'
        }
        return bridge.SetValue('cmi.success_status', mapped.setSuccess)
      }

      if (key === 'cmi.core.session_time') {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return 'false'
        }
        const ms = cmiTimespanToMs(value)
        if (ms === null) {
          adapterError = 405
          return 'false'
        }
        return bridge.SetValue('cmi.session_time', msToIso8601Duration(ms))
      }

      if (key === 'cmi.core.total_time' || key.endsWith('._children')) {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return 'false'
        }
        adapterError = 403
        return 'false'
      }

      return bridge.SetValue(SCORM12_TO_2004_KEYS[key] ?? key, value)
    },

    LMSCommit: (param: string = '') => {
      adapterError = null
      return bridge.Commit(param)
    },

    LMSGetLastError: () => String(lastErrorCode()),
    LMSGetErrorString: (code: string) => SCORM12_ERROR_STRINGS[Number.parseInt(code, 10)] ?? 'Unknown error',
    LMSGetDiagnostic: (code: string) => SCORM12_ERROR_STRINGS[Number.parseInt(code, 10)] ?? 'Unknown error',
  }
}
